import type {
  ActivityEntry, AppRelease, Agent, Connector, ConnectorStatus, OcppFrame, ChargePoint,
} from './types';
import { OCCUPIED_STATUSES } from './types';

/*
 * The console's only data source.
 *
 * Two rules this file exists to hold. It must not invent values the server does
 * not have — a fabricated tariff on a real charge point is worse than a blank,
 * because it looks authoritative. And it must not pretend a request succeeded
 * when it failed, which is why nothing here falls back to a local value.
 *
 * Charge point state comes from CitrineOS; agents, the audit trail, releases
 * and operator overrides come from the BFF's own database. Both arrive through
 * the same origin, so the console never learns which is which.
 */

export const BFF = (import.meta.env.VITE_BFF_URL as string | undefined)
  ?? 'https://ev-bff.nexdecade.com';

export type Source = 'live' | 'unreachable';

/* --------------------------------------------------------------- wire ----- */

export interface LiveChargePoint {
  /** CSMS row id, null for a unit that has been pre-registered and has never
   *  connected. Never the key — `chargePointId` is. */
  id: number | null;
  chargePointId: string;
  /** Raw CSMS flag. Kept for reference; never used to decide anything, because
   *  it sticks at true after any ungraceful disconnect. */
  isOnline: boolean;
  presence: 'online' | 'offline' | 'stale' | 'never';
  silentFor: number | null;
  vendor: string | null;
  model: string | null;
  /** The operator's value where recorded, the charger's report otherwise. */
  serialNumber?: string | null;
  /** What the unit itself said at boot, before any operator correction. */
  reported?: { vendor: string | null; model: string | null; serialNumber: string | null };
  /** False for hardware whose connector status says nothing about whether a
   *  session is running (the YCL reports Available mid-charge). Served from the
   *  server's model policy table; absent from an older BFF. */
  trustsConnectorStatus?: boolean;
  firmware: string | null;
  lastSeenAt: string | null;
  /** The operator's name for it, already resolved server-side. */
  name: string;
  station: LiveSite | null;
  blocked: boolean;
  blockReason: string | null;
  blockedBy: string | null;
  blockedAt: string | null;
  maxSimultaneous: number | null;
  priceMinor: string | number | null;
  currency: string | null;
  preRegistered: boolean;
  /** 'prod' means drivers can see it in the app. 'test' means console only. */
  environment: 'test' | 'prod';
  connectors: Array<{
    id: number;
    status: string | null;
    errorCode: string | null;
    type: string | null;
    powerType: string | null;
    maxWatts: number | null;
    label: string | null;
    /** False when only we know about this bay — the charger has never
     *  mentioned it. Shown with its state unknown rather than not shown. */
    reported: boolean;
    overridden: boolean;
  }>;
  /** Sessions open on this unit right now, so a stop can be offered without
   *  a second round trip. */
  activeTransactions: Array<{
    transactionId: string;
    stationId: number;
    connectorId: number | null;
    startTime: string | null;
    chargingState: string | null;
    totalKwh: string | number | null;
    /** When the charger last sampled its meter. Null before the first sample:
     *  a session seconds old has no reading yet, which is not the same as a
     *  session whose meter has stopped reporting. */
    meterAt: string | null;
  }>;
  /** How the last finished session on this unit ended. A refused idTag opens
   *  and closes a transaction inside a second, so it is never caught in the
   *  open-session list -- this is the only place the reason survives. */
  lastStop: {
    transactionId: string;
    reason: string | null;
    at: string | null;
    kwh: number | null;
  } | null;
  /** How the CSMS answered the last StartTransaction. A refusal leaves no
   *  Transactions row at all, so this is the only record it happened. */
  lastStart: {
    at: string | null;
    status: string | null;
    transactionId: string | null;
    idTag: string | null;
    accepted: boolean;
  } | null;
  origin: Origin | null;
}

export interface UsableIdTag {
  tag: string;
  kind: string;
  driverName: string | null;
  vehicleName: string | null;
  plate: string | null;
  connector: string | null;
  /** 'issued' -- we gave it to a driver's vehicle. 'csms' -- a card loaded
   *  straight into CitrineOS that belongs to no driver here. Either starts a
   *  charge; only the first is ours to revoke. */
  source: 'issued' | 'csms';
}

/** A command sent to a charger. Accepted means the CSMS put it on the wire,
 *  never that the charger did it — that arrives later as OCPP traffic. */
export interface CommandResult {
  accepted: boolean;
  raw: unknown;
  status: number;
  /** Set when the console made the idTag valid before sending. Null when the
   *  CSMS already accepted it. */
  authorized?: {
    tag: string;
    created: boolean;
    previousStatus: string | null;
  } | null;
}

export interface UsageBucket {
  key: string; label: string; sessions: number; kwh: number;
  seconds: number; timed: number; remote: number; open: number;
  first: string | null; last: string | null;
}

export interface UsageSession {
  transactionId: string;
  chargePoint: string;
  chargePointId: string | null;
  connectorId: number | null;
  idTag: string | null;
  /** Resolved from the idTag through our own records. Null when the tag
   *  belongs to nobody — which means the session cannot be billed. */
  driver: string | null;
  vehicle: string | null;
  plate: string | null;
  startTime: string | null;
  endTime: string | null;
  seconds: number | null;
  kwh: number;
  /** The charger's register read lower at the end than the start, so `kwh` is
   *  not the energy delivered — it is unknown. */
  meterReversed: boolean;
  reportedKwh: number | null;
  cost: number | null;
  currency: string | null;
  /** False when the cost was worked out from the charge point's tariff rather
   *  than billed by the CSMS. */
  costFromCsms: boolean;
  stoppedReason: string | null;
  remote: boolean;
  isActive: boolean;
}

export interface UsageReport {
  window: { from: string | null; to: string | null };
  totals: {
    sessions: number; open: number; remoteStarted: number;
    kwh: number; seconds: number; timedSessions: number;
    /** Null where nothing was priced — which is not the same as zero earned —
     *  and also where the window spans more than one currency. */
    cost: number | null;
    currency: string | null;
    mixedCurrencies: boolean;
    pricedSessions: number;
    chargePoints: number; idTags: number;
    drivers: number;
    /** Sessions whose idTag maps to no driver here, so nobody can be billed. */
    unattached: number;
    firstAt: string | null; lastAt: string | null;
  };
  byChargePoint: UsageBucket[];
  byConnector: UsageBucket[];
  byIdTag: UsageBucket[];
  byDriver: UsageBucket[];
  byVehicle: UsageBucket[];
  byDay: UsageBucket[];
  anomalies: {
    open: string[];
    veryLong: Array<{ transactionId: string; hours: number }>;
    zeroEnergy: string[];
    meterReversed: Array<{
      transactionId: string; reported: number;
      chargePointId: string | null; at: string | null;
    }>;
    noIdTag: number;
  };
  sessions: UsageSession[];
}

/** What removing a charge point would actually do, asked before anyone is
 *  invited to confirm it. */
export interface RemovalPreflight {
  chargePointId: string;
  name: string;
  existsInCsms: boolean;
  preRegistered: boolean;
  presence: 'online' | 'offline' | 'stale' | 'never';
  /** Removable with nothing lost that cannot come back. */
  canRemove: boolean;
  /** Removable, but something irreplaceable goes with it. */
  canRemoveWithRecords: boolean;
  /** Impossible, whatever anyone types. */
  blockers: string[];
  /** Possible, but destroys something that cannot be recovered. */
  warnings: string[];
  transactions: number;
  csms: Array<{ table: string; column: string; rows: number }>;
  ours: { overrides: number; connectors: number; connections: number };
  willReturnIfItConnects: boolean;
}

/** A site, as the server sends it. */
export interface LiveSite {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  lat: number | null;
  lng: number | null;
  timezone: string;
  /** 'prod' puts the site, and its production charge points, in the driver app.
   *  The station governs: a charge point marked live on a test site stays
   *  hidden. */
  environment: 'test' | 'prod';
  notes: string | null;
  chargePoints?: number;
  createdBy?: string | null;
  createdAt?: string;
  updatedBy?: string | null;
  updatedAt?: string;
}

export interface LiveTransaction {
  transactionId: string;
  stationId: number;
  isActive: boolean;
  chargingState: string | null;
  totalKwh: string | number | null;
  /** CitrineOS's own column. Nothing writes it; kept only so nothing that
   *  still reads it breaks. Use `cost` and `billed` instead. */
  totalCost: string | number | null;
  stoppedReason: string | null;
  startTime: string | null;
  endTime: string | null;
  connectorId: number | null;
  chargePointId?: string | null;
  /**
   * What the charge came to, in major units.
   *
   * From our own ledger when `billed` is true — that is the figure a driver
   * was actually charged. Otherwise worked out from the charge point's tariff,
   * which is all that can be said about a session that predates billing. Null
   * when there is no tariff to compute from.
   */
  cost?: number | null;
  /** True only when real money moved. An estimate must never be shown as one. */
  billed?: boolean;
  currency?: string;
}

export interface LiveDriver {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: string;
  docStatus: string;
  phoneVerified: boolean;
  createdAt: string;
  vehicles: number;
  sessions: number;
  /** Summed from the ledger, in minor units, as a string — a 64-bit total does
   *  not survive a JavaScript number. */
  balanceMinor: string;
  /** The Terms version this driver agreed to, and when; null if never. */
  termsVersion?: number | null;
  termsAcceptedAt?: string | null;
  /** The current Terms version, to tell "agreed" from "agreed to an old one". */
  termsCurrent?: number | null;
  /** Set when the driver deleted the account from the app. */
  closedAt?: string | null;
}

export interface LiveDispute {
  id: string;
  reason: string;
  note: string;
  state: string;
  resolutionNote: string | null;
  createdAt: string;
  resolvedAt: string | null;
  driverName: string;
  stationId: string;
  connectorId: number;
  energyKwh: number | null;
  costMinor: string | null;
}

export interface IdTag {
  id: string;
  tag: string;
  kind: 'virtual' | 'rfid';
  status: 'active' | 'deactivated';
  createdAt: string;
  deactivatedAt: string | null;
}

export interface DriverVehicle {
  id: string;
  name: string;
  plate: string;
  connector: string;
  archivedAt: string | null;
  tags: IdTag[];
}

export interface DriverDetail {
  driver: {
    id: string; name: string; email: string | null; phone: string | null;
    status: string; docStatus: string; createdAt: string;
  };
  vehicles: DriverVehicle[];
  /** What the CSMS says about each tag, keyed by token. The two disagreeing is
   *  the failure this screen exists to catch. */
  csms: Record<string, { status: string; idTokenType: string | null; updatedAt: string }>;
}

export interface DriftEntry {
  kind: 'missing' | 'status' | 'stale' | 'duplicate';
  tag: string;
  plate?: string;
  driver?: string;
  csmsStatus?: string;
  expected?: string;
  detail: string;
}

export interface AuthReport {
  checked: number;
  csmsRows: number;
  drift: DriftEntry[];
  orphans: Array<{ tag: string; status: string; type: string | null; updatedAt: string }>;
  healthy: boolean;
}

export interface ConfigEntry {
  key: string;
  secret: boolean;
  label: string;
  hint: string;
  /** Null for a secret: its value never leaves the server. */
  value: string | null;
  /** Whether a secret is set. Null for anything that is not a secret. */
  isSet: boolean | null;
  /** Last six characters of a set secret — enough to tell two keys apart,
   *  not enough to use. Null for a non-secret or an unset one. */
  tail: string | null;
  updatedBy: string | null;
  updatedAt: string;
}

/** Where a charge point connects from. Null until it has connected at least
 *  once since the proxy started recording. */
export interface Origin {
  chargePointId: string;
  ip: string;
  ipLastSeen: string;
  connections: number;
  country: string | null;
  countryCode: string | null;
  /** MaxMind calls it a subdivision; it is the district / division / state
   *  depending on where you are. */
  district: string | null;
  city: string | null;
  /** True when the database has no row for this address — a private range or an
   *  unallocated block. Different from "not looked up yet". */
  notFound: boolean;
}

export interface GeoDbStatus {
  present: boolean;
  path: string;
  updatedAt: string | null;
}

export interface LiveConnection {
  id: number;
  chargePointId: string | null;
  isOnline: boolean;
  presence: 'online' | 'offline' | 'stale' | 'never';
  silentFor: number | null;
  protocol: string | null;
  vendor: string | null;
  model: string | null;
  lastSeenAt: string | null;
  origin: Origin | null;
}

/* ------------------------------------------------------------ transport --- */

/*
 * The access token lives here and nowhere else — not in localStorage, not in a
 * cookie the page can read. In web storage it would be readable by any script
 * that ever gets injected; in a module variable it dies with the tab, and the
 * httpOnly refresh cookie is what brings a session back after a reload.
 *
 * Who is acting is no longer something the client asserts. It used to travel as
 * an `x-actor` header, which made the audit trail exactly as trustworthy as its
 * least honest client; the server now reads it from the verified token.
 */
let accessToken: string | null = null;
export function setAccessToken(t: string | null) {
  accessToken = t;
}

/** Called when the server says the session is gone, so the app can drop back to
 *  the sign-in screen instead of showing empty tables. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export class ApiError extends Error {
  /** `message` stays the bare code so the many `e.message === 'x'` checks keep
   *  working. `detail` is the server's own sentence, when it wrote one — some
   *  refusals (connector_status_unreliable, unknown_model) explain themselves
   *  far better than any generic translation of the code could. */
  constructor(message: string, readonly status: number, readonly detail?: string) {
    super(message);
  }
}

async function req<T>(path: string, init?: RequestInit, timeoutMs = 8000): Promise<T> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const r = await fetch(`${BFF}${path}`, {
      ...init,
      signal: ctl.signal,
      // Sends the refresh cookie on the auth calls. Everything else ignores it.
      credentials: 'include',
      headers: {
        ...(init?.body ? { 'content-type': 'application/json' } : {}),
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        ...init?.headers,
      },
    });
    if (!r.ok) {
      // The server's own reason, when it gave one — "reason_required" is far
      // more use on screen than "500".
      const body = await r.json().catch(() => null) as { error?: string; detail?: string } | null;
      if (r.status === 401 && !path.startsWith('/v1/admin/auth')) onUnauthorized?.();
      throw new ApiError(
        body?.error ?? `${path} → ${r.status}`, r.status,
        typeof body?.detail === 'string' ? body.detail : undefined);
    }
    return (await r.json()) as T;
  } finally {
    clearTimeout(t);
  }
}

export interface AuthResult {
  accessToken: string;
  user: {
    sub: string; email: string; name: string;
    role: 'superadmin' | 'operator' | 'support';
    mustChangePassword: boolean;
  };
  screens: string[];
}

/** What a sign-in returns when the console is configured to ask for a code.
 *  No token and no session yet — the password was right, nothing more. */
export interface OtpRequired {
  otpRequired: true;
  email: string;
  expiresInMinutes: number;
}

export const isOtpRequired = (r: AuthResult | OtpRequired): r is OtpRequired =>
  'otpRequired' in r;

export const auth = {
  login: (email: string, password: string) =>
    post<AuthResult | OtpRequired>('/v1/admin/auth/login', { email, password }),
  /** Second step, when codes are on. */
  otp: (email: string, code: string) =>
    post<AuthResult>('/v1/admin/auth/otp', { email, code }),
  /** Always succeeds, whether or not the address has an account — see the
   *  server. The console must not report anything that distinguishes them. */
  forgot: (email: string) =>
    post<{ ok: true; expiresInMinutes: number }>('/v1/admin/auth/forgot', { email }),
  reset: (email: string, code: string, password: string) =>
    post<{ ok: true }>('/v1/admin/auth/reset', { email, code, password }),
  refresh: () => post<AuthResult>('/v1/admin/auth/refresh', {}),
  logout: () => post<{ ok: true }>('/v1/admin/auth/logout', {}),
  changePassword: (current: string, next: string) =>
    post<{ ok: true }>('/v1/admin/auth/password', { current, next }),
};

export interface MailLogEntry {
  id: string;
  to: string;
  subject: string;
  purpose: string;
  ok: boolean;
  messageId: string | null;
  error: string | null;
  ms: number | null;
  at: string;
}

/** A cash top-up awaiting, or past, a decision. */
/** A document attached to one of a driver's cars. */
export interface VehicleDocument {
  id: string;
  kind: string;
  state: 'pending' | 'verified' | 'rejected' | 'superseded';
  note: string | null;
  bytes: number;
  contentType: string;
  rejectReason: string | null;
  uploadedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  vehicleId: string;
  vehicleName: string;
  plate: string;
  driverId: string;
  driverName: string;
}

/** A coupler this network uses. */
export interface ConnectorType {
  code: string;
  label: string;
  position: number;
  active: boolean;
}

/**
 * A charger model this network runs.
 *
 * Not a label: `model` is what a charge point stores and what the server keys
 * a model policy off, so it is picked from this list rather than typed.
 */
export interface ChargerModel {
  model: string;
  manufacturer: string;
  /** What the unit calls itself over OCPP, which is capped at 20 characters. */
  reportedModel: string | null;
  powerKw: number | null;
  connectors: number | null;
  notes: string | null;
  active: boolean;
  /** How many charge points name it. */
  inUse: number;
}

/** A document a driver has sent to be verified. */
export interface DriverDocument {
  id: string;
  kind: string;
  state: 'pending' | 'verified' | 'rejected' | 'superseded';
  bytes: number;
  contentType: string;
  rejectReason: string | null;
  uploadedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  driverId: string;
  driverName: string;
  phone: string | null;
  email: string | null;
}

export interface AdminTopUp {
  id: string;
  amountMinor: number;
  currency: string;
  state: 'awaiting_agent' | 'confirmed' | 'rejected' | 'expired';
  rejectReason: string | null;
  requestedAt: string;
  settledAt: string | null;
  settledBy: string | null;
  driverId: string;
  driverName: string;
  driverEmail: string | null;
  agentId: string | null;
  agentName: string | null;
  /** What the driver holds now, summed from the ledger. */
  balanceMinor: number;
}

/** A counter asking the operator for more float. */
export interface AgentFloatRequest {
  id: string;
  agentId: string;
  agentName: string;
  area: string;
  amountMinor: number;
  currency: string;
  state: 'awaiting_admin' | 'approved' | 'rejected' | 'cancelled';
  note: string | null;
  rejectReason: string | null;
  creditedMinor: number | null;
  requestedAt: string;
  settledAt: string | null;
  settledBy: string | null;
}

/** What one counter holds, summed from its float ledger. */
export interface AgentFloatAgent {
  id: string;
  name: string;
  area: string;
  approval: string;
  floatMinor: number;
}

export interface ConsoleUser {
  id: string;
  email: string;
  name: string;
  role: 'superadmin' | 'operator' | 'support';
  status: 'active' | 'suspended';
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdBy: string | null;
  createdAt: string;
  activeSessions: number;
}

const post = <T>(path: string, body: unknown) =>
  req<T>(path, { method: 'POST', body: JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  req<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const put = <T>(path: string, body: unknown) =>
  req<T>(path, { method: 'PUT', body: JSON.stringify(body) });
const del = <T>(path: string) => req<T>(path, { method: 'DELETE' });

export const live = {
  /* `entries` is optional because /health does not send it — the console
     printed "undefined audit entries" for as long as it assumed otherwise. */
  health: () => req<{ ok: boolean; entries?: number }>('/health', undefined, 4000),

  /* Email. `verify` proves the settings reach the server and authenticate;
     `test` proves a message actually goes out. They fail for different reasons
     and the difference is the whole diagnostic value. */
  /* Tells every open driver app to check for an update now, instead of at its
     next launch. `force` arms the forced update in the same call - arming
     without pushing, or pushing without arming, are the two ways this goes
     wrong and both look like "the update did not work". */
  pushUpdate: (platform: string, force = false) =>
    post<{ sent: number; connected: number; forced: boolean }>(
      `/v1/admin/releases/${platform}/push`, { force }),
  appsConnected: () => req<{ connected: number }>('/v1/admin/app/connected'),

  topUps: () => req<{ items: AdminTopUp[] }>('/v1/admin/topups').then((r) => r.items),

  /* The couplers this network uses. One list, read by the console and both
     apps, so adding one does not need an app release. */
  connectorTypes: () =>
    req<{ items: { code: string; label: string; active: boolean }[] }>(
      '/v1/app/connector-types').then((r) => r.items),

  /* Documents a driver attached to one of their cars. */
  vehicleDocuments: (state: string) =>
    req<{ items: VehicleDocument[] }>(
      `/v1/admin/vehicle-documents?state=${encodeURIComponent(state)}`).then((r) => r.items),
  vehicleDocumentImage: async (id: string): Promise<string> => {
    const r = await fetch(`${BFF}/v1/admin/vehicle-documents/${id}/file`, {
      credentials: 'include',
      headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
    });
    if (!r.ok) throw new Error('Could not load that document.');
    return URL.createObjectURL(await r.blob());
  },
  decideVehicleDocument: (id: string, v: { approve: boolean; reason?: string }) =>
    req<{ id: string; state: string }>(`/v1/admin/vehicle-documents/${id}/decision`, {
      method: 'POST', body: JSON.stringify(v),
    }),

  /* The couplers, for the editor: retired ones included. */
  allConnectorTypes: () =>
    req<{ items: ConnectorType[] }>('/v1/admin/connector-types').then((r) => r.items),
  addConnectorType: (v: { code: string; label: string; position: number }) =>
    req<ConnectorType>('/v1/admin/connector-types', {
      method: 'POST', body: JSON.stringify(v),
    }),
  editConnectorType: (
    code: string,
    v: { label?: string; position?: number; active?: boolean },
  ) =>
    req<ConnectorType>(`/v1/admin/connector-types/${encodeURIComponent(code)}`, {
      method: 'PATCH', body: JSON.stringify(v),
    }),

  /* The charger models: retired ones included, because a charge point may
     still name one and the form has to be able to show it. */
  allChargerModels: () =>
    req<{ items: ChargerModel[] }>('/v1/admin/charger-models').then((r) => r.items),
  addChargerModel: (v: {
    model: string; manufacturer: string; reportedModel?: string;
    powerKw?: number | null; connectors?: number | null; notes?: string;
  }) =>
    req<ChargerModel>('/v1/admin/charger-models', {
      method: 'POST', body: JSON.stringify(v),
    }),
  editChargerModel: (
    model: string,
    v: {
      manufacturer?: string; reportedModel?: string; powerKw?: number | null;
      connectors?: number | null; notes?: string; active?: boolean;
    },
  ) =>
    req<ChargerModel>(`/v1/admin/charger-models/${encodeURIComponent(model)}`, {
      method: 'PATCH', body: JSON.stringify(v),
    }),

  /* Driver identity documents awaiting a decision. */
  documents: (state: string) =>
    req<{ items: DriverDocument[] }>(
      `/v1/admin/documents?state=${encodeURIComponent(state)}`).then((r) => r.items),
  /* The image, fetched rather than linked.
     These are identity documents, so the route needs the console's token —
     which an <img src> cannot carry. The caller revokes the URL when done. */
  documentImage: async (id: string): Promise<string> => {
    const r = await fetch(`${BFF}/v1/admin/documents/${id}/file`, {
      credentials: 'include',
      headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
    });
    if (!r.ok) throw new Error('Could not load that document.');
    return URL.createObjectURL(await r.blob());
  },
  decideDocument: (id: string, v: { approve: boolean; reason?: string }) =>
    req<{ id: string; state: string }>(`/v1/admin/documents/${id}/decision`, {
      method: 'POST', body: JSON.stringify(v),
    }),

  /* Float: credit a counter buys before it can give any away. */
  agentFloat: () => req<{
    requests: AgentFloatRequest[]; agents: AgentFloatAgent[]; currency: string;
  }>('/v1/admin/agent-float'),
  decideAgentFloat: (
    id: string,
    v: { approve: boolean; amountMinor?: number; reason?: string },
  ) => post<{ ok: true; state: string }>(`/v1/admin/agent-float/${id}/decision`, v),
  grantAgentFloat: (
    id: string,
    v: { amountMinor: number; memo?: string },
  ) => post<{ ok: true; floatMinor: number }>(
    `/v1/admin/agents/${encodeURIComponent(id)}/float`, v),
  decideTopUp: (
    id: string,
    v: { approve: boolean; amountMinor?: number; reason?: string },
  ) => post<{ ok: true; state: string }>(`/v1/admin/topups/${id}/decision`, v),

  mailVerify: () => post<{ ok: boolean; error?: string }>('/v1/admin/mail/verify', {}),
  mailTest: (to?: string) =>
    post<{ ok: boolean; error?: string; messageId?: string }>('/v1/admin/mail/test', { to }),
  mailLog: (limit = 30) => req<{ items: MailLogEntry[] }>(`/v1/admin/mail/log?limit=${limit}`),

  /* Three levels, three calls. `stations` is the site level — the one nothing
     else creates; `chargepoints` is the hardware standing on it. */
  stations: () => req<{ items: LiveSite[] }>('/v1/admin/stations'),
  chargePoints: () =>
    req<{ items: LiveChargePoint[]; geoDb: GeoDbStatus }>('/v1/admin/chargepoints'),
  transactions: (limit = 100) =>
    req<{ items: LiveTransaction[] }>(`/v1/admin/transactions?limit=${limit}`),
  connections: () => req<{ items: LiveConnection[]; geoDb: GeoDbStatus }>('/v1/admin/connections'),
  origins: () => req<{ items: Origin[]; geoDb: GeoDbStatus }>('/v1/admin/origins'),
  refreshOrigins: () => post<{ recorded: number; resolved: number; db: boolean }>(
    '/v1/admin/origins/refresh', {}),

  frames: (q: { limit?: number; action?: string; direction?: string; chargePointId?: string } = {}) => {
    const p = new URLSearchParams();
    p.set('limit', String(q.limit ?? 200));
    if (q.action && q.action !== 'all') p.set('action', q.action);
    if (q.direction && q.direction !== 'all') p.set('direction', q.direction);
    if (q.chargePointId && q.chargePointId !== 'all') p.set('chargePointId', q.chargePointId);
    return req<{ items: OcppFrame[] }>(`/v1/admin/ocpp/frames?${p}`);
  },
  frameActions: () => req<{ items: Array<{ action: string; n: number }> }>('/v1/admin/ocpp/actions'),

  agents: () => req<{ items: Agent[] }>('/v1/admin/agents'),
  activity: (q: { area?: string; limit?: number; q?: string } = {}) => {
    const p = new URLSearchParams();
    p.set('limit', String(q.limit ?? 300));
    if (q.area && q.area !== 'all') p.set('area', q.area);
    if (q.q) p.set('q', q.q);
    return req<{ items: ActivityEntry[] }>(`/v1/admin/activity?${p}`);
  },
  /** The usage log: per-route counts, latency, errors and app builds. */
  apiUsage: (hours: number) =>
    req<ApiUsageReport>(`/v1/admin/usage/api?hours=${hours}`),
  /** Terms & Conditions and Privacy Policy, as the driver app shows them. */
  legal: () => req<{ items: LegalDoc[] }>('/v1/admin/legal'),
  /** Publishes a new version; the old one is kept. */
  publishLegal: (kind: LegalDoc['kind'], v: { title: string; body: string; askAgain: boolean }) =>
    put<{ ok: true; version: number; askAgain: boolean; carried: number }>(`/v1/admin/legal/${kind}`, v),
  activitySummary: () =>
    req<{ areas: Array<{ area: string; n: number }>; total: number; day: number }>(
      '/v1/admin/activity/summary'),
  releases: () => req<{ items: AppRelease[] }>('/v1/admin/releases'),

  drivers: (q = '') =>
    req<{ items: LiveDriver[] }>(`/v1/admin/drivers${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  disputes: (state = 'all') =>
    req<{ items: LiveDispute[] }>(`/v1/admin/disputes?state=${encodeURIComponent(state)}`),
  config: () => req<{ items: ConfigEntry[] }>('/v1/admin/config'),
  users: () => req<{ items: ConsoleUser[] }>('/v1/admin/users'),
  driver: (id: string) => req<DriverDetail>(`/v1/admin/drivers/${id}`),
  authorizations: () => req<AuthReport>('/v1/admin/authorizations'),

  /* ------------------------------------------------------------ writes --- */

  createStation: (v: Partial<LiveSite> & { name: string }) =>
    post<LiveSite>('/v1/admin/stations', v),
  updateStation: (id: string, v: Partial<LiveSite>) =>
    patch<LiveSite>(`/v1/admin/stations/${id}`, v),
  archiveStation: (id: string) =>
    del<{ ok: true; released: number }>(`/v1/admin/stations/${id}`),

  /* Charge points are addressed by the identity they announce, never by the
     CSMS row id — a pre-registered one has no row id at all. */
  blockChargePoint: (cpId: string, blocked: boolean, reason = '') =>
    post<{ ok: true }>(`/v1/admin/chargepoints/${encodeURIComponent(cpId)}/block`,
      { blocked, reason }),
  updateChargePoint: (
    cpId: string,
    v: {
      stationId?: string | null; displayName?: string | null;
      maxSimultaneous?: number | null; priceMinor?: number | null; currency?: string | null;
      environment?: 'test' | 'prod';
      manufacturer?: string | null; model?: string | null; serialNumber?: string | null;
    },
  ) => patch<{ ok: true }>(`/v1/admin/chargepoints/${encodeURIComponent(cpId)}`, v),
  preRegisterChargePoint: (v: { chargePointId: string; displayName?: string; stationId?: string }) =>
    post<{ ok: true; chargePointId: string }>('/v1/admin/chargepoints', v),

  saveConnector: (
    cpId: string, connectorId: number,
    v: {
      label?: string | null; connectorType?: string | null;
      powerType?: 'AC' | 'DC' | null; maxWatts?: number | null; declared?: boolean;
    },
  ) => put<{ ok: true }>(
    `/v1/admin/chargepoints/${encodeURIComponent(cpId)}/connectors/${connectorId}`, v),
  usableIdTags: () => req<{ items: UsableIdTag[] }>('/v1/admin/idtags'),
  usage: (q: { from?: string; to?: string } = {}) => {
    const p = new URLSearchParams();
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
    const qs = p.toString();
    return req<UsageReport>(`/v1/admin/reports/usage${qs ? `?${qs}` : ''}`, undefined, 15000);
  },
  remoteStart: (
    cpId: string,
    v: { idTag: string; connectorId?: number; authorizeUnknown?: boolean },
  ) =>
    post<CommandResult>(
      `/v1/admin/chargepoints/${encodeURIComponent(cpId)}/remote-start`, v),
  setChargerConfig: (cpId: string, key: string, value: string | number) =>
    post<CommandResult>(
      `/v1/admin/chargepoints/${encodeURIComponent(cpId)}/config`, { key, value }),
  /** What the charger itself reported, not the CSMS's receipt for the send. */
  chargerConfig: (cpId: string, keys?: string[]) =>
    req<{
      accepted: true;
      configurationKey: Array<{ key: string; value?: string; readonly: boolean }>;
      unknownKey: string[];
    }>(
      `/v1/admin/chargepoints/${encodeURIComponent(cpId)}/config`
      + (keys?.length ? `?keys=${encodeURIComponent(keys.join(','))}` : ''),
      undefined, 15000),

  remoteStop: (cpId: string, transactionId: string) =>
    post<CommandResult>(
      `/v1/admin/chargepoints/${encodeURIComponent(cpId)}/remote-stop`, { transactionId }),

  removalPreflight: (cpId: string) =>
    req<RemovalPreflight>(`/v1/admin/chargepoints/${encodeURIComponent(cpId)}/removal`),
  /** `includeRecords` takes the charge point's sessions with it, and the
   *  server refuses without it when there are any. */
  removeChargePoint: (cpId: string, confirm: string, includeRecords = false) =>
    req<{ ok: true; removedCsms: number; removedTransactions: number }>(
      `/v1/admin/chargepoints/${encodeURIComponent(cpId)}`,
      { method: 'DELETE', body: JSON.stringify({ confirm, includeRecords }) }),

  /** Close a session the charger has forgotten, settling it at the last meter
   *  value it managed to report. Refused while the charger says the bay is in
   *  a session. */
  closeLostSession: (cpId: string, transactionId: string, force = false) =>
    req<{
      ok: true; transactionId: string; connectorId: number | null;
      totalKwh: number; settledAt: string | null; anomaly: string | null;
    }>(`/v1/admin/chargepoints/${encodeURIComponent(cpId)}/close-session`,
      { method: 'POST', body: JSON.stringify({ transactionId, force }) }),

  removeConnector: (cpId: string, connectorId: number) =>
    del<{ ok: true }>(
      `/v1/admin/chargepoints/${encodeURIComponent(cpId)}/connectors/${connectorId}`),

  createAgent: (v: {
    name: string; area: string;
    commissionMode?: 'percent' | 'fixed'; commissionPct?: number; commissionFixed?: number;
  }) => post<Agent>('/v1/admin/agents', v),
  decideAgent: (id: string, approval: 'approved' | 'declined' | 'pending', reason = '') =>
    post<Agent>(`/v1/admin/agents/${id}/decision`, { approval, reason }),

  setConfig: (v: Record<string, string>) => patch<{ ok: true }>('/v1/admin/config', v),

  createDriver: (v: { name: string; email?: string; phone?: string }) =>
    post<{ id: string; name: string; temporaryPassword: string }>('/v1/admin/drivers', v),
  updateDriver: (id: string, v: { status?: string; docStatus?: string }) =>
    patch<{ name: string; status: string }>(`/v1/admin/drivers/${id}`, v),
  addVehicle: (driverId: string, v: { name: string; plate: string; connector: string }) =>
    post<{ id: string; plate: string; idTag: string }>(`/v1/admin/drivers/${driverId}/vehicles`, v),
  /* Cards only. The virtual tag is issued when the vehicle is created and is
     not editable: rotate and reactivate are gone, and revoking is one-way
     because a card is revoked when it is lost. */
  issueCard: (vehicleId: string, tag: string) =>
    post<{ tag: string; kind: string }>(`/v1/admin/vehicles/${vehicleId}/idtags`,
      { kind: 'rfid', tag }),
  revokeCard: (id: string, reason?: string) =>
    post<{ ok: true; tag: string }>(`/v1/admin/idtags/${id}/revoke`, { reason }),

  createUser: (v: { email: string; name: string; role: string }) =>
    post<{ email: string; temporaryPassword: string }>('/v1/admin/users', v),
  updateUser: (id: string, v: { role?: string; status?: string }) =>
    patch<ConsoleUser>(`/v1/admin/users/${id}`, v),

  updateRelease: (
    app: 'driver' | 'agent',
    platform: 'android' | 'ios',
    v: { forceUpdate?: boolean; rollout?: number; published?: boolean; minSupported?: string; notes?: string },
  ) => patch<AppRelease>(`/v1/admin/releases/${app}/${platform}`, v),

  /** Upload an Android build. The file is the request body, streamed; ten
   *  minutes, because 50 MB over a slow office uplink is not eight seconds. */
  uploadBuild: (app: 'driver' | 'agent', file: File, v: { version: string; build: number; notes?: string }) =>
    req<AppRelease>(
      `/v1/admin/app-builds/${app}/android?${new URLSearchParams({
        version: v.version, build: String(v.build), ...(v.notes ? { notes: v.notes } : {}),
      })}`,
      { method: 'POST', body: file, headers: { 'content-type': 'application/vnd.android.package-archive' } },
      10 * 60_000,
    ),

  /** A counter's Spark Agent login. The password comes back once. */
  issueAgentLogin: (id: string, v: { email?: string; phone?: string }) =>
    post<{ id: string; email: string | null; phone: string | null; counterCode: string; password: string }>(
      `/v1/admin/agents/${id}/login`, v),
  updateAgentCounter: (id: string, v: {
    counterName?: string | null; city?: string | null; address?: string | null;
    email?: string | null; phone?: string | null;
    lat?: number | null; lng?: number | null;
  }) => patch<unknown>(`/v1/admin/agents/${id}`, v),
  reissueAgentCode: (id: string) =>
    post<{ counterCode: string }>(`/v1/admin/agents/${id}/code`, {}),
};

/* ------------------------------------------------------------- shaping --- */

/**
 * An OCPP StatusNotification state, as the console's own word for it.
 *
 * Matched exactly rather than by substring, and every one of the nine 1.6
 * states is listed. The previous version tested for 'charging', 'available' and
 * 'fault' and let everything else fall through to 'offline' — which took a
 * charge point that had just booted, reported `Preparing` and was heartbeating
 * every sixty seconds, and drew it as a dead bay.
 *
 * Anything unrecognised is 'offline' too, but that is now a narrow case: a 2.0.1
 * state we have not mapped, or a row with no status at all. It never means the
 * charger said something we chose to ignore.
 */
const OCPP_STATUS: Record<string, ConnectorStatus> = {
  available: 'available',
  preparing: 'preparing',
  charging: 'charging',
  suspendedev: 'suspended',
  suspendedevse: 'suspended',
  finishing: 'finishing',
  reserved: 'reserved',
  unavailable: 'unavailable',
  faulted: 'faulted',
  // OCPP 2.0.1 collapses the occupied states into one word.
  occupied: 'charging',
};

function mapStatus(s: string | null): ConnectorStatus {
  return OCPP_STATUS[(s ?? '').trim().toLowerCase()] ?? 'offline';
}

/**
 * Shapes a CSMS charge point into the console's ChargePoint, with the operator's
 * overrides laid on top.
 *
 * A blocked charge point keeps reporting itself online — blocking is a
 * commercial decision, not a hardware state — so `blocked` is carried alongside
 * `status` rather than folded into it, and every count that matters reads
 * `freeNow`, which accounts for both.
 */
export function toChargePoint(s: LiveChargePoint): ChargePoint {
  /* The CSMS keeps the last StatusNotification for ever, so a charge point that
     died on Tuesday still has a connector row saying Available. That row is not
     a lie, it is just old — but drawing it green would be. A bay's reported
     state only means anything while the charge point is talking to us. */
  const reachable = s.presence === 'online';
  const connectors: Connector[] = s.connectors.map((c) => ({
    id: c.id,
    type: c.type,
    // Null, not 1. Rounding an unknown wattage to "1 kW" invented a number the
    // operator then had to disprove.
    powerKw: c.maxWatts ? Math.round(c.maxWatts / 100) / 10 : null,
    current: c.powerType
      ? (c.powerType.toUpperCase().includes('DC') ? 'DC' : 'AC')
      : null,
    // A bay only we know about has no reported state at all.
    status: !c.reported ? 'offline' : reachable ? mapStatus(c.status) : 'offline',
    label: c.label,
    reported: c.reported,
  }));

  const price = s.priceMinor != null ? Number(s.priceMinor) / 100 : 0;

  return {
    // Keyed by the OCPP identity, not the CSMS row id — see the type.
    id: s.chargePointId,
    name: s.name,
    station: s.station
      ? {
        id: s.station.id, name: s.station.name, city: s.station.city,
        address: s.station.address, lat: s.station.lat, lng: s.station.lng,
      }
      : null,
    preRegistered: s.preRegistered,
    environment: s.environment ?? 'test',
    csmsId: s.id,
    vendor: s.vendor, model: s.model, firmware: s.firmware,
    /* These three were sent and dropped here, which left the Rename dialog
       unable to show the serial or what the charger itself reported, and left
       the test bench blind to the model policy — it kept offering to close a
       live YCL session the server would never close (2026-10-23). Undefined
       from an older BFF, and every reader treats undefined as "trusted". */
    serialNumber: s.serialNumber ?? null,
    reported: s.reported,
    trustsConnectorStatus: s.trustsConnectorStatus,
    /* Presence, not `isOnline`. The flag is set on connect and only cleared on
       a clean disconnect, so it survives a dropped link, an ungraceful close
       and a container restart — this network had one charge point flagged
       online after six days of silence and another that had never sent a
       message. */
    status: reachable
      ? (connectors.some((c) => OCCUPIED_STATUSES.includes(c.status)) ? 'in_use' : 'available')
      : 'offline',
    chargePointId: s.chargePointId,
    presence: s.presence,
    silentFor: s.silentFor,
    price,
    currency: s.currency ?? undefined,
    // Every connector independent unless the operator has said otherwise: a
    // shared-power limit is site knowledge the CSMS does not hold.
    maxSimultaneous: s.maxSimultaneous ?? Math.max(1, connectors.length),
    blocked: s.blocked,
    blockReason: s.blockReason ?? undefined,
    blockedBy: s.blockedBy ?? undefined,
    blockedAt: s.blockedAt ?? undefined,
    origin: s.origin ?? undefined,
    activeTransactions: s.activeTransactions ?? [],
    lastStop: s.lastStop ?? null,
    lastStart: s.lastStart ?? null,
    connectors,
  };
}

/** GET /v1/admin/usage/api — see spark_ev_bff/src/lib/request_log.ts. */
export interface LegalDoc {
  kind: 'terms' | 'privacy' | 'agent_terms';
  version?: number;
  /** False for a version published as a correction nobody had to agree to again. */
  requiresConsent?: boolean;
  /** Drivers (terms) or counters (agent_terms) with an open account. */
  people?: number;
  title?: string;
  body?: string;
  updatedAt?: string;
  updatedBy?: string;
  /** Who has agreed to this version: drivers for the Terms, counters for the
   *  Counter Terms. Absent for the Privacy Policy, which is not agreed to on
   *  its own. */
  acceptedCurrent?: number;
}

export interface ApiUsageReport {
  hours: number;
  totals: { calls: number; callers: number; errors5xx: number; errors4xx: number };
  routes: Array<{ method: string; route: string; calls: number; errors5xx: number;
    errors4xx: number; p50: number; p95: number; max_ms: number }>;
  versions: Array<{ app: string; version: string; calls: number; callers: number;
    errors: number; last_seen: string }>;
  errors: Array<{ at: string; method: string; route: string; status: number; ms: number;
    caller_kind: string | null; caller_id: string | null; app_version: string | null }>;
}
